import React, {FC, useEffect, useState} from "react"
import Spacing from "@irati/ui/src/Spacing"
import {Body, H2, H3, H4, H6, Label} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import BaseSvgIcon from "@irati/ui/src/Icon/customIcons/Base"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import mainTheme from "@irati/ui/src/global/theme"
import Hr from "@irati/ui/src/Layout/Hr"
import authentication from "@irati/auth"
import {PaymentCurrencyType, WalletEntityType, WellnessMedia, Wallet} from "@irati/types"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import TreatsTriggerModal from "@irati-web/wallet/src/components/TreatsModal/TreatsTriggerModal"
import {Card} from "@irati/ui/src/Layout"
import {ValidationButton} from "@irati/ui/src/FormItem"
import ErrorText from "@irati/ui/src/ErrorText"
import History from "@irati/utils/history"
import walletService from "@irati/wallet/graph/wallet"
import wellnessService from "@irati/wellness/services/Wellness"
import paymentService from "@irati/payments/services/Payments"
import tracking from "@irati-tracking/wellness"
import Loading from "@irati/ui/src/Loading"
import {getFormattedRentalPeriodUnit} from "@irati/wellness/utils"
import DetailsPill from "../MediaCard/DetailsPill"

interface Props {
  media: WellnessMedia
}

const MediaRedemptionSection: FC<Props> = ({media}) => {
  const [wallet, setWallet] = useState<Wallet>({})
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [submit, setSubmit] = useState<Promise<any> | null>(null)

  const fetchWallet = async() => {
    const data = await walletService
      .get(authentication.user().uuid, PaymentCurrencyType.Tt, WalletEntityType.User, {fetchPolicy: "network-only"})
    setWallet(data)
    return data
  }

  useEffect(() => {
    setLoad(fetchWallet())
  }, [])


  const handleSubmit = () => {
    if (wallet.availableBalance >= media.price) {
      tracking.startRentMedia(media)

      const {uuid} = media
      const variables = {mediaId: uuid}

      setSubmit(
        wellnessService.createMediaRental(variables).then(async({paymentOrder}) => {
          return paymentService.payByWellnessWallet(paymentOrder, wallet, {amount: paymentOrder.amount}, media).then(() => {
            return wellnessService.completeMediaRental({mediaId: uuid}).then(() => {
              tracking.completeRentMedia(media)
              History.replace({
                pathname: `/wellness/details/${media?.uuid}`
              })
            })
          })
        })
      )
    }
  }

  return (
    <Card flush paddingBottom={2}>
      <Spacing padding={4}>
        <H3 marginBottom>{media?.title}</H3>
        <Label large>by <b>{media?.author?.name}</b></Label>
        <Flex marginTop>
          <Flex alignItems="center" marginRight={3}>
            <BaseSvgIcon viewBox="0 0 30 30" width={14} height={14} marginRight>
              <path d={media?.category?.svgData} />
            </BaseSvgIcon>
            <Body>{media?.category?.mediaLabel}</Body>
          </Flex>
          <Flex alignItems="center">
            <DetailsPill text={media.duration} type={media.type} />
          </Flex>
        </Flex>
        <Hr />
        <Flex justifyContent="space-between" alignItems="center">
          <Body>
            <FormattedMessage
              id="wellness.rentalMedia.redemption.mediaDuration"
              values={{
                period: media?.rentalPeriod,
                periodUnit: getFormattedRentalPeriodUnit(media?.rentalPeriod, media?.rentalPeriodUnit)
              }} />
          </Body>
          <Flex alignItems="center">
            <Icon icon={IconTypes.flexiPoints} size={16} />
            <Body marginLeft>{media?.price}</Body>
          </Flex>
        </Flex>
        <Hr />
        <Flex justifyContent="space-between" alignItems="center">
          <H6>
            <FormattedMessage id="wellness.rentalMedia.redemption.totalToPay" />
          </H6>
          <Flex alignItems="center">
            <Icon icon={IconTypes.flexiPoints} size={28} />
            <H2 marginLeft>{media?.price}</H2>
          </Flex>
        </Flex>
      </Spacing>
      <Loading load={load}>
        <Flex style={{background: mainTheme.colors.utility.highlight}} direction="column" alignItems="center" marginBottom={2}>
          <H4 marginBottom={2} marginTop={2}>
            <FormattedMessage id="wellness.rentalMedia.yourPerkboxWallet" />
          </H4>
          <Flex direction="column" alignItems="center" marginBottom>
            <Label>
              <FormattedMessage id="wellness.rentalMedia.available" />
            </Label>
            <Flex>
              <H2>
                <FormattedCurrency value={wallet.availableBalance} isCents={false} currency={wallet.currency} />
              </H2>
              <TreatsTriggerModal />
            </Flex>
          </Flex>
          {wallet.availableBalance >= media.price ?
            <Label>
              <FormattedMessage
                id="wellness.rentalMedia.redemption.pointsLeftOver"
                values={{count: wallet.availableBalance - media.price}} />
            </Label> :
            <ErrorText>
              <FormattedMessage id="wellness.rentalMedia.redemption.notEnoughPointsError" />
            </ErrorText>
          }
          <ValidationButton primary marginTop={2} marginBottom={2} onClick={handleSubmit} load={submit}>
            <FormattedMessage id="wellness.rentalMedia.static.createRental.cta" values={{price: media?.price}} />
          </ValidationButton>
        </Flex>
      </Loading>
    </Card>
  )
}


export default MediaRedemptionSection
